/**
 * Server-backed shadow + lighting config, shared between the /insects viewer
 * and the game. Viewer POSTs updates; game fetches once at boot and applies
 * to its sun + hemi lights + renderer exposure.
 */

export interface ShadowConfig {
  sunIntensity: number;
  ambientIntensity: number;
  shadowRadius: number;
  shadowBias: number;
  sunElevationDeg: number;
  sunAzimuthDeg: number;
  exposure: number;
}

export const SHADOW_DEFAULTS: ShadowConfig = {
  sunIntensity: 6.0,
  ambientIntensity: 0.15,
  shadowRadius: 3,
  shadowBias: -0.0005,
  sunElevationDeg: 58,
  sunAzimuthDeg: 40,
  exposure: 1.0,
};

const API_URL = '/api/shadow';

let cache: ShadowConfig = { ...SHADOW_DEFAULTS };
let loaded = false;

export async function loadShadowConfig(): Promise<ShadowConfig> {
  try {
    const res = await fetch(API_URL, { cache: 'no-cache' });
    if (res.ok) {
      const j = await res.json();
      if (j && typeof j === 'object') {
        cache = { ...SHADOW_DEFAULTS, ...(j as Partial<ShadowConfig>) };
      }
    }
  } catch (err) {
    console.warn('loadShadowConfig failed:', err);
  }
  loaded = true;
  return cache;
}

export function getShadowConfig(): ShadowConfig {
  return cache;
}

export function isShadowLoaded(): boolean {
  return loaded;
}

export async function saveShadowConfig(cfg: ShadowConfig): Promise<void> {
  cache = cfg;
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cfg),
  });
  if (!res.ok) throw new Error(`POST /api/shadow → ${res.status}`);
}
